import {useEffect, useState} from 'react'
import {Helmet} from 'react-helmet'
import {Typography, Row, Col, Card, Tag, Button, Input, Empty, Skeleton, Modal, message, Divider} from 'antd'
import {SearchOutlined, PlusOutlined, CrownOutlined, LockOutlined, CheckOutlined} from '@ant-design/icons'
import * as API_DISCOVER from '../../../../shared/http/discover'
import {fetchWorldLists} from '../../../../store/wordLists/extraReducers'
import {useAppDispatch, useAppSelector} from '../../../../store/hooks'
import {userSelector} from '../../../../store/global'
import Constants from '../../../../const/Constants'
import {DiscoverResponse, DoneCardListShort, DoneCardListFull, CategorySection, DoneWord} from '../../../../shared/types/api'
import '../../../../styles/scss/done-word-lists/done-word-lists.scss'

const {Title, Text, Paragraph} = Typography

const LEVEL_COLORS: Record<string, string> = {
    A1: 'green',
    A2: 'cyan',
    B1: 'blue',
    B2: 'geekblue',
    C1: 'purple',
    C2: 'magenta'
}

const LANG_LABELS: Record<string, string> = {
    RUSSIAN: 'Русский',
    ENGLISH: 'Английский',
    SPANISH: 'Испанский',
    GERMAN: 'Немецкий',
    ITALIAN: 'Итальянский',
    JAPANESE: 'Японский',
    KOREAN: 'Корейский',
    TURKISH: 'Турецкий',
    CHINESE: 'Китайский',
    FRENCH: 'Французский',
    ARABIC: 'Арабский',
}

const DoneWordLists = () => {
    const dispatch = useAppDispatch()
    const user = useAppSelector(userSelector)
    const isPremiumUser = !!user?.isPremium

    const [discover, setDiscover] = useState<DiscoverResponse | null>(null)
    const [loading, setLoading] = useState<boolean>(true)
    const [addedIds, setAddedIds] = useState<Set<number>>(new Set())

    const [searchResults, setSearchResults] = useState<DoneCardListShort[] | null>(null)
    const [searchLoading, setSearchLoading] = useState<boolean>(false)
    const [searchQuery, setSearchQuery] = useState<string>('')

    const [selectedLang, setSelectedLang] = useState<string | null>(null)
    const [selectedLevel, setSelectedLevel] = useState<string | null>(null)

    const [selectedList, setSelectedList] = useState<DoneCardListFull | null>(null)
    const [selectedListLoading, setSelectedListLoading] = useState<boolean>(false)
    const [modalOpen, setModalOpen] = useState<boolean>(false)

    const [copyingId, setCopyingId] = useState<number | null>(null)

    useEffect(() => {
        API_DISCOVER.getDiscover()
            .then((data: DiscoverResponse) => {
                setDiscover(data)
                if (data.addedDoneListIds) {
                    setAddedIds(new Set(data.addedDoneListIds))
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false))
    }, [])

    const handleSearch = (value: string) => {
        if (!value || !value.trim()) {
            setSearchResults(null)
            return
        }
        setSearchLoading(true)
        API_DISCOVER.searchLists(value, selectedLang ?? undefined, selectedLevel ?? undefined, 0, 50)
            .then((data: any) => setSearchResults(data?.content || []))
            .catch(() => setSearchResults([]))
            .finally(() => setSearchLoading(false))
    }

    const openListDetail = (id: number) => {
        setModalOpen(true)
        setSelectedListLoading(true)
        API_DISCOVER.getFullList(id)
            .then((data: DoneCardListFull) => setSelectedList(data))
            .catch(() => message.error('Не удалось загрузить список'))
            .finally(() => setSelectedListLoading(false))
    }

    const copyList = (id: number, isPremiumList?: boolean) => {
        if (isPremiumList && !isPremiumUser) {
            message.warning('Этот список доступен только для Premium пользователей')
            return
        }
        if (addedIds.has(id)) {
            message.info('Список уже добавлен в ваш словарь')
            return
        }
        setCopyingId(id)
        API_DISCOVER.copyListToUser(id, 'RUSSIAN')
            .then(() => {
                message.success('Список добавлен в ваш словарь')
                setAddedIds(prev => new Set(prev).add(id))
                dispatch(fetchWorldLists())
            })
            .catch(() => message.error('Ошибка при копировании'))
            .finally(() => setCopyingId(null))
    }

    const isLocked = (list: DoneCardListShort) => !!list.isPremium && !isPremiumUser
    const isAdded = (id: number) => addedIds.has(id)

    const renderActionButton = (list: DoneCardListShort) => {
        if (isAdded(list.id)) {
            return (
                <Button type="link" icon={<CheckOutlined/>} style={{color: 'var(--color-success)'}}>
                    Добавлено
                </Button>
            )
        }
        if (isLocked(list)) {
            return (
                <Button type="link" icon={<LockOutlined/>} style={{color: 'var(--color-warning-dark)'}}>
                    Premium
                </Button>
            )
        }
        return (
            <Button
                type="link"
                icon={<PlusOutlined/>}
                loading={copyingId === list.id}
                onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    copyList(list.id, list.isPremium)
                }}
            >
                Добавить
            </Button>
        )
    }

    const renderListCard = (list: DoneCardListShort) => (
        <Col xs={24} sm={12} md={8} lg={6} key={list.id}>
            <Card
                hoverable
                style={{
                    height: '100%',
                    ...(list.isPremium ? {borderColor: 'var(--color-warning)', borderWidth: 2} : {}),
                    ...(isAdded(list.id) ? {opacity: 0.75} : {})
                }}
                onClick={() => openListDetail(list.id)}
                cover={list.treeId ? (
                    <div style={{display: 'flex', justifyContent: 'center', padding: '16px 0 0'}}>
                        <img
                            src={`${Constants.queryConst}/v1/trees/image/${list.treeId}/JUST_PLANTED`}
                            alt="tree"
                            style={{width: 80, height: 80, objectFit: 'contain'}}
                        />
                    </div>
                ) : undefined}
                actions={[renderActionButton(list)]}
            >
                <div style={{marginBottom: 8}}>
                    {list.level && (
                        <Tag color={LEVEL_COLORS[list.level] || 'default'}>{list.level}</Tag>
                    )}
                    {list.sourceLanguage && (
                        <Tag>{LANG_LABELS[list.sourceLanguage] || list.sourceLanguage}</Tag>
                    )}
                    {list.isPremium && <Tag icon={<CrownOutlined/>} color="gold">Premium</Tag>}
                </div>
                <Card.Meta
                    title={list.title}
                    description={
                        <div>
                            {list.description && (
                                <Paragraph ellipsis={{rows: 2}} style={{marginBottom: 4}}>
                                    {list.description}
                                </Paragraph>
                            )}
                            {list.wordCount != null && (
                                <Text type="secondary">{list.wordCount} слов</Text>
                            )}
                        </div>
                    }
                />
                {list.tags && list.tags.length > 0 && (
                    <div style={{marginTop: 8}}>
                        {list.tags.map((tag: string) => (
                            <Tag key={tag} style={{marginBottom: 4}}>{tag}</Tag>
                        ))}
                    </div>
                )}
            </Card>
        </Col>
    )

    const renderHero = (hero: DoneCardListShort) => {
        const heroLocked = isLocked(hero)
        const heroAdded = isAdded(hero.id)
        const heroBg = hero.isPremium
            ? 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        const heroTextColor = hero.isPremium ? '#5a3e00' : '#fff'

        let heroBtn
        if (heroAdded) {
            heroBtn = (
                <Button size="large" icon={<CheckOutlined/>}
                        style={{background: 'var(--color-bg-surface)', color: 'var(--color-success)', border: 'none', fontWeight: 600}}>
                    Добавлено
                </Button>
            )
        } else if (heroLocked) {
            heroBtn = (
                <Button size="large" icon={<LockOutlined/>}
                        style={{background: 'var(--color-bg-surface)', color: 'var(--color-warning-dark)', border: 'none', fontWeight: 600}}>
                    Premium
                </Button>
            )
        } else {
            heroBtn = (
                <Button
                    type="primary" size="large" icon={<PlusOutlined/>}
                    loading={copyingId === hero.id}
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation()
                        copyList(hero.id, hero.isPremium)
                    }}
                    style={{background: 'var(--color-bg-surface)', color: hero.isPremium ? 'var(--color-warning-dark)' : '#764ba2', border: 'none', fontWeight: 600}}
                >
                    Добавить
                </Button>
            )
        }

        return (
            <Card
                style={{marginBottom: 24, background: heroBg, border: 'none'}}
                onClick={() => openListDetail(hero.id)}
                hoverable
            >
                <div className="hero-row">
                    <div className="hero-content">
                        <div style={{marginBottom: 8}}>
                            <Tag color="gold">Список недели</Tag>
                            {hero.isPremium && <Tag icon={<CrownOutlined/>} color="gold">Premium</Tag>}
                        </div>
                        <Title level={3} style={{color: heroTextColor, margin: 0}}>{hero.title}</Title>
                        {hero.description && (
                            <Paragraph style={{color: hero.isPremium ? 'rgba(90,62,0,0.75)' : 'rgba(255,255,255,0.85)', marginTop: 8, marginBottom: 8}}>
                                {hero.description}
                            </Paragraph>
                        )}
                        <div>
                            {hero.level && <Tag color={LEVEL_COLORS[hero.level]}>{hero.level}</Tag>}
                            {hero.wordCount != null && <Tag>{hero.wordCount} слов</Tag>}
                        </div>
                    </div>
                    <div className="hero-action">{heroBtn}</div>
                </div>
            </Card>
        )
    }

    const renderCategorySection = (section: CategorySection) => {
        const filtered = section.lists?.filter(filterList) || []
        if (filtered.length === 0) return null
        return (
            <div key={section.category.id} style={{marginBottom: 32}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12}}>
                    <Title level={4} style={{margin: 0}}>
                        {section.category.title}
                    </Title>
                </div>
                <Row gutter={[16, 16]}>
                    {filtered.map(renderListCard)}
                </Row>
            </div>
        )
    }

    if (loading) {
        return (
            <div style={{padding: 24}}>
                <Skeleton active paragraph={{rows: 3}}/>
                <Row gutter={[16, 16]} style={{marginTop: 24}}>
                    {[...Array(4)].map((_, i) => (
                        <Col xs={24} sm={12} md={8} lg={6} key={i}>
                            <Card><Skeleton active/></Card>
                        </Col>
                    ))}
                </Row>
            </div>
        )
    }

    const filterList = (list: DoneCardListShort) => {
        if (selectedLang && list.sourceLanguage !== selectedLang) return false
        if (selectedLevel && list.level !== selectedLevel) return false
        return true
    }

    const filteredAllLists = discover?.allLists?.filter(filterList) || []
    const hasSections = discover?.sections && discover.sections.length > 0
    const hasAllLists = filteredAllLists.length > 0
    const isEmpty = !discover || (!discover.hero && !hasSections && !hasAllLists)

    const showHero = discover?.hero && filterList(discover.hero)

    return (
        <div style={{padding: 24}}>
            <Helmet title='Готовые списки карточек'/>
            <Title level={2}>Готовые списки</Title>

            {/* Поиск */}
            <Input.Search
                placeholder="Поиск списков..."
                prefix={<SearchOutlined/>}
                onSearch={handleSearch}
                onChange={(e) => setSearchQuery(e.target.value)}
                value={searchQuery}
                allowClear
                style={{maxWidth: 400, marginBottom: 16}}
                loading={searchLoading}
            />

            {/* Фильтр по языку */}
            {discover?.languages && discover.languages.length > 0 && (
                <div style={{marginBottom: 8}}>
                    <Text type="secondary" style={{marginRight: 8}}>Язык:</Text>
                    <Tag
                        color={!selectedLang ? 'blue' : 'default'}
                        style={{cursor: 'pointer'}}
                        onClick={() => setSelectedLang(null)}
                    >
                        Все
                    </Tag>
                    {discover.languages.map((lang: string) => (
                        <Tag
                            key={lang}
                            color={selectedLang === lang ? 'blue' : 'default'}
                            style={{cursor: 'pointer'}}
                            onClick={() => setSelectedLang(selectedLang === lang ? null : lang)}
                        >
                            {LANG_LABELS[lang] || lang}
                        </Tag>
                    ))}
                </div>
            )}

            {/* Фильтр по уровню */}
            {discover?.levels && discover.levels.length > 0 && (
                <div style={{marginBottom: 24}}>
                    <Text type="secondary" style={{marginRight: 8}}>Уровень:</Text>
                    <Tag
                        color={!selectedLevel ? 'blue' : 'default'}
                        style={{cursor: 'pointer'}}
                        onClick={() => setSelectedLevel(null)}
                    >
                        Все
                    </Tag>
                    {discover.levels.map((level: string) => (
                        <Tag
                            key={level}
                            color={selectedLevel === level ? (LEVEL_COLORS[level] || 'blue') : 'default'}
                            style={{cursor: 'pointer'}}
                            onClick={() => setSelectedLevel(selectedLevel === level ? null : level)}
                        >
                            {level}
                        </Tag>
                    ))}
                </div>
            )}

            {searchResults !== null ? (
                <div>
                    <Title level={4}>Результаты поиска</Title>
                    {searchResults.length === 0 ? (
                        <Empty description="Ничего не найдено"/>
                    ) : (
                        <Row gutter={[16, 16]}>
                            {searchResults.map(renderListCard)}
                        </Row>
                    )}
                    <Button
                        type="link"
                        onClick={() => {
                            setSearchResults(null)
                            setSearchQuery('')
                        }}
                        style={{marginTop: 8}}
                    >
                        Вернуться к каталогу
                    </Button>
                </div>
            ) : isEmpty ? (
                <Empty description="Каталог пока пуст"/>
            ) : (
                <>
                    {showHero && renderHero(discover!.hero!)}
                    {hasSections
                        ? discover?.sections?.map(renderCategorySection)
                        : hasAllLists && (
                            <Row gutter={[16, 16]}>
                                {filteredAllLists.map(renderListCard)}
                            </Row>
                        )
                    }
                </>
            )}

            <Modal
                open={modalOpen}
                onCancel={() => {
                    setModalOpen(false)
                    setSelectedList(null)
                }}
                footer={selectedList ? [
                    <Button key="cancel" onClick={() => setModalOpen(false)}>Закрыть</Button>,
                    isAdded(selectedList.id) ? (
                        <Button key="added" icon={<CheckOutlined/>} style={{color: 'var(--color-success)', borderColor: 'var(--color-success)'}}>
                            Уже добавлено
                        </Button>
                    ) : selectedList.isPremium && !isPremiumUser ? (
                        <Button key="premium" icon={<LockOutlined/>} style={{color: 'var(--color-warning-dark)', borderColor: 'var(--color-warning-dark)'}}>
                            Доступно в Premium
                        </Button>
                    ) : (
                        <Button
                            key="copy"
                            type="primary"
                            icon={<PlusOutlined/>}
                            loading={copyingId === selectedList?.id}
                            onClick={() => copyList(selectedList.id, selectedList.isPremium)}
                        >
                            Добавить в словарь
                        </Button>
                    )
                ] : null}
                width={600}
                title={selectedList?.title}
            >
                {selectedListLoading ? (
                    <Skeleton active paragraph={{rows: 6}}/>
                ) : selectedList ? (
                    <div>
                        <div style={{marginBottom: 12}}>
                            {selectedList.level && <Tag color={LEVEL_COLORS[selectedList.level]}>{selectedList.level}</Tag>}
                            {selectedList.sourceLanguage && <Tag>{LANG_LABELS[selectedList.sourceLanguage] || selectedList.sourceLanguage}</Tag>}
                            {selectedList.wordCount != null && <Tag>{selectedList.wordCount} слов</Tag>}
                            {selectedList.isPremium && <Tag icon={<CrownOutlined/>} color="gold">Premium</Tag>}
                        </div>
                        {selectedList.description && (
                            <Paragraph>{selectedList.description}</Paragraph>
                        )}
                        <Divider/>
                        <div style={{maxHeight: 400, overflow: 'auto'}}>
                            {selectedList.words && selectedList.words.length > 0 ? (
                                selectedList.words.map((word: DoneWord) => (
                                    <div key={word.id} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '8px 0',
                                        borderBottom: '1px solid var(--color-bg-muted)'
                                    }}>
                                        <Text strong>{word.sourceText}</Text>
                                        <Text type="secondary">
                                            {word.translations?.map(t => t.translation).join(', ')}
                                        </Text>
                                    </div>
                                ))
                            ) : (
                                <Empty description="Нет слов"/>
                            )}
                        </div>
                    </div>
                ) : null}
            </Modal>
        </div>
    )
}

export default DoneWordLists